/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 */

import { motion } from "motion/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";

gsap.registerPlugin(ScrollTrigger);
export function PageHeader({
	eyebrow,
	title,
	description,
	meta,
	align = "left",
	className,
}: {
	eyebrow?: string;
	title: string;
	description?: string;
	meta?: string[];
	align?: "left" | "center";
	className?: string;
}) {
	const alignClass =
		align === "center" ? "mx-auto text-center items-center" : "items-start";

	const childVariants = {
		hidden: { opacity: 0, y: 18, filter: "blur(4px)" },
		visible: {
			opacity: 1,
			y: 0,
			filter: "blur(0px)",
			transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
		},
	};

	return (
		<motion.div
			initial="hidden"
			animate="visible"
			variants={{
				hidden: {},
				visible: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
			}}
			className={cn("flex max-w-3xl flex-col gap-4", alignClass, className)}>
			{/* Eyebrow badge */}
			{eyebrow && (
				<motion.div
					variants={childVariants}
					className="inline-flex items-center gap-2">
					{/* Pulsing accent dot */}
					<span className="relative flex h-2 w-2">
						<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#f9a93a] opacity-60" />
						<span className="relative inline-flex h-2 w-2 rounded-full bg-[#f9a93a]" />
					</span>
					<span className="inline-flex items-center rounded-full border border-[#2d2f92]/25 bg-white/80 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#2d2f92] shadow-sm backdrop-blur-sm">
						{eyebrow}
					</span>
				</motion.div>
			)}

			{/* Title with gradient accent underline */}
			<motion.div
				variants={childVariants}
				className="relative">
				<h1 className="text-3xl font-semibold tracking-[-0.04em]  sm:text-4xl lg:text-[2.65rem] lg:leading-[1.18]">
					{title}
				</h1>
				{/* Decorative underline */}
				<motion.div
					className="mt-3 h-[3px] w-16 rounded-full"
					style={{
						background: "linear-gradient(90deg, #2d2f92, #f9a93a)",
					}}
					initial={{ scaleX: 0, originX: 0 }}
					animate={{ scaleX: 1 }}
					transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
				/>
			</motion.div>

			{description && (
				<motion.p
					variants={childVariants}
					className="text-sm leading-[1.85] text-slate-500 sm:text-[15px]">
					{description}
				</motion.p>
			)}

			{/* Meta tags */}
			{meta && meta.length > 0 && (
				<motion.div
					variants={childVariants}
					className="flex flex-wrap gap-2">
					{meta.map((tag) => (
						<span
							key={tag}
							className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 shadow-sm">
							{tag}
						</span>
					))}
				</motion.div>
			)}
		</motion.div>
	);
}
